import { useQueryClient } from '@tanstack/react-query';
import {
  MessagingChannelType,
  postV1MessagingChannels,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import { MessagingChannel } from '~hooks/useMessaging/models';
import {
  buildChannelCreatePayload,
  isTargetFieldRejection,
} from '~hooks/useMessaging/useStartConversation';
import {
  MESSAGING_CHANNELS_QUERY_KEY,
  messagingTargetChannelQueryKey,
  MessagingTargetChannelType,
} from '~queries/messagingQueryKeys';
import connection from '~services/connectionModule';

/** Raised untoasted: the caller shows it in place, since the job page cannot hide it up front. */
export class MessagingJobDriverUnreachableError extends Error {
  constructor() {
    super("The job's driver cannot be messaged");
    this.name = 'MessagingJobDriverUnreachableError';
  }
}

type OpenTargetChannelInput = {
  channelType: MessagingTargetChannelType;
  name: string;
  targetId: string;
};

/**
 * Create is open-or-create for a job or order: a live channel comes back as itself with the
 * caller added as a member, so this is safe to call without knowing whether one exists.
 */
export default function useOpenTargetChannel() {
  const queryClient = useQueryClient();

  const openTargetChannel = useCallback(
    async ({ channelType, name, targetId }: OpenTargetChannelInput) => {
      try {
        const response = await postV1MessagingChannels({
          body: buildChannelCreatePayload({
            channelType,
            name,
            ...(channelType === MessagingChannelType.JOB
              ? { jobId: targetId }
              : { orderId: targetId }),
          }),
        });
        const channel = MessagingChannel.parse(response.data.data);

        queryClient.setQueryData(
          messagingTargetChannelQueryKey(channelType, targetId),
          channel,
        );
        await queryClient.invalidateQueries({ queryKey: MESSAGING_CHANNELS_QUERY_KEY });

        return channel;
      } catch (error) {
        // With open-or-create, a `job_id` rejection means the job has no driver who can be messaged.
        const isJobDriverUnreachable =
          channelType === MessagingChannelType.JOB &&
          isTargetFieldRejection(error, MessagingChannelType.JOB);
        if (isJobDriverUnreachable) {
          throw new MessagingJobDriverUnreachableError();
        }

        connection.handleRequestError(
          error,
          t('error_messages.messaging.failed_to_create_channel'),
        );
        throw error;
      }
    },
    [queryClient],
  );

  return { openTargetChannel };
}
